import type {
	CallCreated,
	CallHistoryDeleteInput,
	CallHistoryDeleteResult,
	CallHistoryViewedResult,
	CallJoinCredentials,
	CallParticipantManage,
	CallStateUpdate,
	ServerCall,
	StartCallInput
} from '@andresot/protocol';
import { authorizedFetch, request } from './client.js';

export async function fetchCalls(): Promise<{
	calls: ServerCall[];
	available: boolean;
	recordingAvailable: boolean;
}> {
	return request('/api/calls');
}

export function markCallsViewed(): Promise<CallHistoryViewedResult> {
	return request('/api/calls/viewed', { method: 'POST', body: {} });
}

export function deleteCallHistory(input: CallHistoryDeleteInput): Promise<CallHistoryDeleteResult> {
	return request('/api/calls/history/delete', { method: 'POST', body: input });
}

export async function createCall(input: StartCallInput): Promise<CallCreated> {
	return request('/api/calls', { method: 'POST', body: input });
}

export async function acceptCall(callId: string): Promise<ServerCall> {
	const response = await request<{ call: ServerCall }>(
		`/api/calls/${encodeURIComponent(callId)}/accept`,
		{
			method: 'POST',
			body: {}
		}
	);
	return response.call;
}

export async function declineCall(callId: string): Promise<ServerCall> {
	const response = await request<{ call: ServerCall }>(
		`/api/calls/${encodeURIComponent(callId)}/decline`,
		{
			method: 'POST',
			body: {}
		}
	);
	return response.call;
}

export async function endCall(callId: string): Promise<ServerCall> {
	const response = await request<{ call: ServerCall }>(
		`/api/calls/${encodeURIComponent(callId)}/end`,
		{
			method: 'POST',
			body: {}
		}
	);
	return response.call;
}

export async function updateCallState(callId: string, state: CallStateUpdate): Promise<ServerCall> {
	const response = await request<{ call: ServerCall }>(
		`/api/calls/${encodeURIComponent(callId)}/state`,
		{
			method: 'PATCH',
			body: state
		}
	);
	return response.call;
}

export function fetchCallCredentials(callId: string): Promise<CallJoinCredentials> {
	return request(`/api/calls/${encodeURIComponent(callId)}/token`, { method: 'POST', body: {} });
}

export async function createCallLink(callId: string): Promise<string> {
	const response = await request<{ url: string }>(
		`/api/calls/${encodeURIComponent(callId)}/share`,
		{
			method: 'POST',
			body: {}
		}
	);
	return response.url;
}

export async function joinCallLink(token: string): Promise<ServerCall> {
	const response = await request<{ call: ServerCall }>('/api/calls/join-link', {
		method: 'POST',
		body: { token }
	});
	return response.call;
}

/** Войти в идущий групповой звонок из его чата — без ссылки. */
export async function joinCall(callId: string): Promise<ServerCall> {
	const response = await request<{ call: ServerCall }>(
		`/api/calls/${encodeURIComponent(callId)}/join`,
		{ method: 'POST', body: {} }
	);
	return response.call;
}

export async function manageCallParticipant(
	callId: string,
	userId: string,
	input: CallParticipantManage
): Promise<ServerCall> {
	const response = await request<{ call: ServerCall }>(
		`/api/calls/${encodeURIComponent(callId)}/participants/${encodeURIComponent(userId)}`,
		{ method: 'PATCH', body: input }
	);
	return response.call;
}

export async function startCallRecording(callId: string): Promise<ServerCall> {
	const response = await request<{ call: ServerCall }>(
		`/api/calls/${encodeURIComponent(callId)}/recording`,
		{ method: 'POST', body: {} }
	);
	return response.call;
}

export async function confirmCallRecordingReady(callId: string): Promise<ServerCall> {
	const response = await request<{ call: ServerCall }>(
		`/api/calls/${encodeURIComponent(callId)}/recording-ready`,
		{ method: 'POST', body: {} }
	);
	return response.call;
}

export async function stopCallRecording(callId: string): Promise<ServerCall> {
	const response = await request<{ call: ServerCall }>(
		`/api/calls/${encodeURIComponent(callId)}/recording`,
		{ method: 'DELETE' }
	);
	return response.call;
}

export async function downloadCallRecording(url: string, filename: string): Promise<void> {
	const response = await authorizedFetch(url, { method: 'GET' });
	if (!response.ok) throw new Error('Не удалось скачать запись звонка');
	const objectUrl = URL.createObjectURL(await response.blob());
	try {
		const link = document.createElement('a');
		link.href = objectUrl;
		link.download = filename;
		document.body.append(link);
		link.click();
		link.remove();
	} finally {
		URL.revokeObjectURL(objectUrl);
	}
}
